import type {
  GalleryResponse,
  PhotoCountResponse,
  SessionResponse,
  LoginRequest,
  LoginResponse,
  UploadInitRequest,
  UploadInitResponse,
  UploadCompleteRequest,
  UploadCompleteResponse,
  DownloadResponse,
  Settings,
  AdminStats,
  AdminPhotosResponse,
  AuditLogResponse,
} from '../types';

const API_BASE = ((import.meta as unknown as { env?: Record<string, string> }).env?.VITE_API_URL || '') + '/api';

async function fetchJson<T>(url: string, options?: RequestInit): Promise<T> {
  const response = await fetch(url, {
    ...options,
    headers: {
      'Content-Type': 'application/json',
      ...options?.headers,
    },
    credentials: 'include',
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({ error: 'Unbekannter Fehler' }));
    throw new Error(error.error || `HTTP ${response.status}`);
  }

  if (response.status === 204) {
    return {} as T;
  }

  return response.json();
}

function getCsrfToken(): string | null {
  const cookies = document.cookie.split(';').map(c => c.trim());
  const csrfCookie = cookies.find(c => c.startsWith('csrf_token='));
  if (csrfCookie) {
    return csrfCookie.split('=')[1].split('.')[0];
  }
  return null;
}

function withCsrf(options?: RequestInit): RequestInit {
  const token = getCsrfToken();
  if (!token) return options || {};

  return {
    ...options,
    headers: {
      ...options?.headers,
      'X-CSRF-Token': token,
    },
  };
}

export const api = {
  auth: {
    login: (data: LoginRequest) =>
      fetchJson<LoginResponse>(`${API_BASE}/auth/login`, withCsrf({ method: 'POST', body: JSON.stringify(data) })),

    adminLogin: (password: string) =>
      fetchJson<LoginResponse>(`${API_BASE}/auth/admin-login`, withCsrf({ method: 'POST', body: JSON.stringify({ password }) })),

    logout: () =>
      fetchJson<{ success: boolean }>(`${API_BASE}/auth/logout`, withCsrf({ method: 'POST' })),

    getSession: () => fetchJson<SessionResponse>(`${API_BASE}/auth/session`),

    refreshCsrf: () => fetchJson<{ csrfToken: string }>(`${API_BASE}/auth/csrf`, withCsrf({ method: 'POST' })),
  },

  photos: {
    getGallery: (page: number = 1, limit: number = 50, order: 'asc' | 'desc' = 'desc') =>
      fetchJson<GalleryResponse>(`${API_BASE}/photos?page=${page}&limit=${limit}&order=${order}`),

    getCount: () => fetchJson<PhotoCountResponse>(`${API_BASE}/photos/count`),

    getPhoto: (id: number) => `${API_BASE}/photos/${id}`,

    getThumbnail: (id: number) => `${API_BASE}/photos/${id}/thumbnail`,

    initUpload: (files: UploadInitRequest['files']) =>
      fetchJson<UploadInitResponse>(`${API_BASE}/photos/upload/init`, withCsrf({ method: 'POST', body: JSON.stringify({ files }) })),

    completeUpload: (data: UploadCompleteRequest) =>
      fetchJson<UploadCompleteResponse>(`${API_BASE}/photos/upload/complete`, withCsrf({ method: 'POST', body: JSON.stringify(data) })),

    abortUpload: (uploadSessionId: string, objectKeys: string[]) =>
      fetchJson<{ success: boolean }>(`${API_BASE}/photos/upload/abort`, withCsrf({ method: 'POST', body: JSON.stringify({ uploadSessionId, objectKeys }) })),

    download: (photoIds: number[]) =>
      fetchJson<DownloadResponse>(`${API_BASE}/photos/download`, withCsrf({ method: 'POST', body: JSON.stringify({ photoIds }) })),

    downloadAll: () => fetchJson<DownloadResponse>(`${API_BASE}/photos/download/all`),
  },

  admin: {
    getStats: () => fetchJson<AdminStats>(`${API_BASE}/admin/stats`),

    getPhotos: (page: number = 1, limit: number = 100, order: 'asc' | 'desc' = 'desc') =>
      fetchJson<AdminPhotosResponse>(`${API_BASE}/admin/photos?page=${page}&limit=${limit}&order=${order}`),

    deletePhoto: (id: number) =>
      fetchJson<{ success: boolean }>(`${API_BASE}/admin/photos/${id}`, withCsrf({ method: 'DELETE' })),

    deleteMultiplePhotos: (ids: number[]) =>
      fetchJson<{ success: boolean; deleted: number }>(`${API_BASE}/admin/photos/delete-multiple`, withCsrf({ method: 'POST', body: JSON.stringify({ ids }) })),

    getSettings: () => fetchJson<{ settings: Settings }>(`${API_BASE}/admin/settings`),

    updateSettings: (settings: Partial<Settings>) =>
      fetchJson<{ success: boolean; settings: Settings }>(`${API_BASE}/admin/settings`, withCsrf({ method: 'PUT', body: JSON.stringify(settings) })),

    changePassword: (currentPassword: string, newPassword: string, isAdmin: boolean) =>
      fetchJson<{ success: boolean; newHash: string; message: string }>(`${API_BASE}/admin/change-password`, withCsrf({ method: 'POST', body: JSON.stringify({ currentPassword, newPassword, isAdmin }) })),

    getAuditLog: (page: number = 1, limit: number = 50) =>
      fetchJson<AuditLogResponse>(`${API_BASE}/admin/audit-log?page=${page}&limit=${limit}`),

    cleanup: () =>
      fetchJson<{ success: boolean; expiredSessions: number; expiredUploads: number }>(`${API_BASE}/admin/cleanup`, withCsrf({ method: 'POST' })),

    deleteAll: (confirmText: string) =>
      fetchJson<{ success: boolean; deleted: number }>(`${API_BASE}/admin/delete-all`, withCsrf({ method: 'POST', body: JSON.stringify({ confirm: true, confirmText }) })),
  },

  status: () => fetchJson<{
    expiryDate: string;
    isExpired: boolean;
    maintenanceMode: boolean;
    maintenanceMessage: string;
    allowUploads: boolean;
    galleryTitle: string;
    gallerySubtitle: string;
    albumName: string;
    privacyNotice: string;
    maxFileSizeMb?: number;
    maxFilesPerUpload?: number;
    maxTotalUploadSizeMb?: number;
  }>(`${API_BASE}/status`),
};

export async function uploadToR2(url: string, file: File, onProgress?: (loaded: number, total: number) => void): Promise<void> {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();

    xhr.upload.addEventListener('progress', (e) => {
      if (e.lengthComputable && onProgress) {
        onProgress(e.loaded, e.total);
      }
    });

    xhr.addEventListener('load', () => {
      if (xhr.status >= 200 && xhr.status < 300) {
        resolve();
      } else {
        reject(new Error(`Upload fehlgeschlagen: ${xhr.status}`));
      }
    });

    xhr.addEventListener('error', () => reject(new Error('Netzwerkfehler beim Upload')));
    xhr.addEventListener('abort', () => reject(new Error('Upload abgebrochen')));

    xhr.open('PUT', url);
    xhr.setRequestHeader('Content-Type', file.type);
    xhr.send(file);
  });
}

export async function computeFileChecksum(file: File): Promise<string> {
  const buffer = await file.arrayBuffer();
  const hashBuffer = await crypto.subtle.digest('SHA-256', buffer);
  const hashArray = Array.from(new Uint8Array(hashBuffer));
  return hashArray.map(b => b.toString(16).padStart(2, '0')).join('');
}

export function getImageDimensions(file: File): Promise<{ width: number; height: number } | null> {
  return new Promise((resolve) => {
    const img = new Image();
    const url = URL.createObjectURL(file);
    img.onload = () => {
      URL.revokeObjectURL(url);
      resolve({ width: img.width, height: img.height });
    };
    img.onerror = () => {
      URL.revokeObjectURL(url);
      resolve(null);
    };
    img.src = url;
  });
}